import React, { useEffect, useState } from "react";
import { RootState } from "../..";
import useAppSelector from "../../hooks/useAppSelector";
import { TReservationType } from "../../store/types";
import "./ReservationTypeImage.scss";
import { SkipassDesktop, SkipassMobile } from "../../assets/reservationTypes/Skipass";
import { RentalDesktop, RentalMobile } from "../../assets/reservationTypes/Rental";
import { LettingDesktop, LettingMobile } from "../../assets/reservationTypes/Letting";
import { ActivityDesktop, ActivityMobile } from "../../assets/reservationTypes/Activity";
import { getOptimizedImageUrlFromImageUrlAndWidth } from "@r360/library";
import { getDynamicPath } from "../../Helper";

type TReservationTypeImage = {
    reservationTypeId: number;
};

type TImage = {
    desktop?: string;
    mobile?: string;
};

export const ReservationTypeImage = ({ reservationTypeId }: TReservationTypeImage) => {
    const lang = useAppSelector(state => state.clientData?.lang);
    const { isMobile } = useAppSelector((state: RootState) => state.window);
    const clientData = useAppSelector((state: RootState) => state.clientData);
    const { type, desktopImage, mobileImage } = clientData.reservationtypes.find(
        (resvType: TReservationType) => resvType.id === reservationTypeId
    ) as TReservationType;
    const [fallbackImageAccessibility, setFallbackImageAccessibility] = useState("");

    const fallbackImage = {
        SKIPASS: {
            desktop: SkipassDesktop,
            mobile: SkipassMobile,
        },
        LETTING: {
            desktop: LettingDesktop,
            mobile: LettingMobile,
        },
        RENTAL: {
            desktop: RentalDesktop,
            mobile: RentalMobile,
        },
        ACTIVITY: {
            desktop: ActivityDesktop,
            mobile: ActivityMobile,
        },
    }[type] as TImage;

    useEffect(() => {
        const ariaLabel = {
            SKIPASS: {
                altText: "Person skiing downhill",
            },
            LETTING: {
                altText: "Ski with ski goggles",
            },
            RENTAL: {
                altText: "Person on a mountain bike cycling down a hill",
            },
            ACTIVITY: {
                altText: "Two kids sledding down a snowy hill",
            },
        }[type];

        if (ariaLabel) {
            setFallbackImageAccessibility(ariaLabel?.altText);
        }
    }, [type]);

    const optimizedDesktopImage = getOptimizedImageUrlFromImageUrlAndWidth(desktopImage?.path, window.innerWidth);
    const optimizedMobileImage = getOptimizedImageUrlFromImageUrlAndWidth(mobileImage?.path, window.innerWidth);

    const getAriaLabels = () => {
        if (isMobile && mobileImage?.translations) {
            return mobileImage.translations?.[lang]?.accessibility || "";
        } else if (!isMobile && desktopImage?.translations) {
            return desktopImage.translations?.[lang]?.accessibility || "";
        } else {
            return fallbackImageAccessibility || "";
        }
    };

    const bgImage = isMobile
        ? optimizedMobileImage || fallbackImage.mobile
        : optimizedDesktopImage || fallbackImage.desktop;

    return (
        <div
            className="reservation-type-image"
            role="img"
            aria-label={getAriaLabels()}
            style={{
                backgroundImage: `url(${getDynamicPath(bgImage)})`,
            }}
        ></div>
    );
};
